/**
 * Wall extraction (pure): walk each walkable polygon's edges in ~1 yd pieces and keep the
 * pieces whose outside isn't walkable (so doorways into corridors stay open).
 */
import type { Polygon, Vec2 } from '@keystone/sim';

export type Segment = [Vec2, Vec2];

export function pointInPolygon(p: Vec2, poly: Polygon): boolean {
  const pts = poly.points;
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i]!;
    const b = pts[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/** Signed area > 0 means counter-clockwise. */
function signedArea(poly: Polygon): number {
  let s = 0;
  const pts = poly.points;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) s += (pts[j]!.x - pts[i]!.x) * (pts[j]!.y + pts[i]!.y);
  return s / 2;
}

export function computeWallSegments(walkable: readonly Polygon[], step = 1, probe = 0.6): Segment[] {
  const out: Segment[] = [];
  for (const poly of walkable) {
    const pts = poly.points;
    if (pts.length < 3) continue;
    const ccw = signedArea(poly) >= 0;
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i]!;
      const b = pts[(i + 1) % pts.length]!;
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      if (len < 1e-6) continue;
      const dx = (b.x - a.x) / len;
      const dy = (b.y - a.y) / len;
      // Outward normal: right of the edge for CCW polygons.
      const nx = ccw ? dy : -dy;
      const ny = ccw ? -dx : dx;
      const n = Math.max(1, Math.round(len / step));
      let runStart: Vec2 | null = null;
      for (let k = 0; k < n; k++) {
        const t0 = (k / n) * len;
        const t1 = ((k + 1) / n) * len;
        const mid = (t0 + t1) / 2;
        const outside = { x: a.x + dx * mid + nx * probe, y: a.y + dy * mid + ny * probe };
        const open = walkable.some((q) => q !== poly && pointInPolygon(outside, q));
        const p0 = { x: a.x + dx * t0, y: a.y + dy * t0 };
        if (!open && !runStart) runStart = p0;
        if (open && runStart) {
          out.push([runStart, p0]);
          runStart = null;
        }
      }
      if (runStart) out.push([runStart, { x: b.x, y: b.y }]);
    }
  }
  return out;
}
